// In-memory demo store for bw-contracts "running request" documents (Req*Contract).
//
// The marketplace product wizards (currently Bibby Factoring; BusinessInsurance /
// FlowpayCredit can be layered on later) drive a single stateful document through
// createDocument → createEntity/updateEntity/deleteEntity → submit. There is no
// backend in demo mode, so this module keeps the document alive in a module-level
// Map and re-serialises it (readEntities-shaped) on every call.
//
// Design notes
// ------------
// * Only ReqCreateBibbyFactoringContract has real lifecycle behaviour today. Any
//   other documentType gets a minimal generic doc so handlers never throw.
// * The FE reads deeply-nested paths (see DocRespReqCreateBibbyFactoringContract in
//   the generated SDK model). We mirror exactly the fields the pages/components
//   touch — see useBibbyFactoringContract.ts, ReviewForm.tsx, etc.
// * Section gating is intentionally permissive: ALL four sections start "Running"
//   so navigation is never blocked; we only flip individual sections to
//   "Completed" as the user advances, which the stepper uses for the check marks.
// * The mock return shape is intentionally loosely typed (the SDK response unions
//   are huge); callers cast as needed.

import { finishDemoOnboarding } from "./demoOnboarding.ts";
import { DEMO_COMPANY_BY_ID, DEMO_COMPANY_HOLDINGS_AG } from "./seed/index.ts";

type Json = Record<string, unknown>;

type StoredDoc = {
  documentId: string;
  documentType: string;
  etag: string;
  ts: number;
  // Bibby-specific running state
  partyRoleRef?: Json;
  partyRoleAdditionalInfo?: Json;
  contactPersonRef?: Json;
  customers: Array<{ entityId: string; entityData: Json }>;
  requestDetails?: Json;
  attachments: Array<{ entityId: string; entityData: Json }>;
  leadId?: string;
  sections: Record<string, SectionState>;
  // The single ProductFormSubmit activity (created up front so the Review
  // submit button is enabled; flipped to Completed on submit).
  submitActivityId: string;
  submitActivityState: "Created" | "Completed";
  // BusinessInsurance-specific running state. A generic bag of single-instance
  // entities keyed by entity name (partyRoleRef, businessArea, coverage*, etc.)
  // — the FE re-reads the whole doc via readEntities on every mutation, so we
  // just merge whatever entityData the caller sends and echo it all back.
  biEntities?: Record<string, Json>;
  // The process / master references the payment page polls on after submit.
  biProcessState?: "Running" | "Completed";
  biMasterDocumentId?: string;
  // FlowpayCredit-specific running state. Single-instance entities keyed by
  // entity name (partyRoleRef, contactPersonRef, flowpayConsent, ...). The FE
  // re-reads the whole doc via readEntities on every mutation, so we merge
  // whatever entityData the caller sends and echo it all back.
  flowpayEntities?: Record<string, Json>;
  flowpayProcessState?: "Running" | "Completed";
};

type SectionState =
  | "Running"
  | "SufficientDataCollected"
  | "Completed"
  | "NotNeeded"
  | "NotStarted";

const BIBBY = "ReqCreateBibbyFactoringContract";
const BI = "ReqCreateBusinessInsuranceContract";
const BI_LIVE = "LiveBusinessInsuranceContract";
const FLOWPAY = "ReqCreateFlowpayCreditContract";

const DEMO_FLOWPAY_CUSTOMER_ID = "demo-flowpay-customer-1";

// Section order for BibbyFactoring (matches stepConfigs in useBibbyFactoringContract.ts).
const BIBBY_SECTIONS = [
  "CompanyDetails",
  "CustomerDetails",
  "FactoringRequest",
  "ReviewRequest",
] as const;

// Section order for BusinessInsurance (matches stepConfigs in useBusinessInsuranceContract.ts).
const BI_SECTIONS = [
  "CompanyParty",
  "BusinessArea",
  "Coverage",
  "Quote",
  "Confirmation",
] as const;

// Section order for FlowpayCredit (matches flowpayStepConfigs in
// flowpayCreditSteps.utils.ts).
const FLOWPAY_SECTIONS = ["CompanyParty", "Consent", "DataSubmission"] as const;

// Single-instance entity keys the FlowpayCredit wizard creates/updates. The FE
// re-reads the whole doc on every mutation, so a generic merge is sufficient.
const FLOWPAY_ENTITY_KEYS = [
  "partyRoleRef",
  "contactPersonRef",
  "flowpayConsent",
] as const;

const FLOWPAY_ENTITY_TYPE_MAP: Record<string, string> = {
  partyRoleRef: "PartyRoleRef",
  partyRoleAdditionalInfo: "PartyRoleAdditionalInfo",
  contactPersonRef: "ContactPersonRef",
  flowpayConsent: "FlowpayConsent",
  flowpayCustomer: "FlowpayCustomer",
};

// Single-instance entity keys the BI wizard creates/updates/deletes. Addresses
// live nested under buildingInsuranceCoverage.entityData.addresses (an array),
// but the entity itself is still a single instance, so generic merge applies.
const BI_ENTITY_KEYS = [
  "partyRoleRef",
  "businessArea",
  "coverageToBeInsured",
  "buildingInsuranceCoverage",
  "inventoryInsuranceCoverage",
  "machineryInsuranceCoverage",
  "businessLiabilityInsuranceCoverage",
  "additionalCoverage",
  "individualOffer",
  "businessInsurancePolicyEssential",
  "businessInsurancePolicySmart",
  "businessInsurancePolicyPremium",
  "coverageContractDetails",
] as const;

const store = new Map<string, StoredDoc>();
let idCounter = 0;
let custCounter = 0;
let attCounter = 0;

function nextDocId(): string {
  idCounter += 1;
  return `demo-contract-${idCounter}-${Date.now()}`;
}

function bumpEtag(doc: StoredDoc) {
  doc.ts = Date.now();
  doc.etag = `"demo-etag-${doc.documentId}-${doc.ts}"`;
}

function getDocId(body: Json): string | undefined {
  const ref = (body.updateDocumentReference ?? body.documentReference) as
    | Json
    | undefined;
  return ref?.documentId as string | undefined;
}

function getDocType(body: Json): string {
  const ref = (body.updateDocumentReference ??
    body.createDocumentReference ??
    body.documentReference) as Json | undefined;
  return (
    (ref?.documentType as string | undefined) ??
    (body.documentType as string | undefined) ??
    BIBBY
  );
}

// ── Public API ────────────────────────────────────────────────────────────────

export function contractsCreateDocument(body: Json): Json {
  const documentType = getDocType(body);
  const documentId = nextDocId();

  if (documentType === BI) {
    const entityData = (body.entityData ?? {}) as Json;
    const partyRoleRef = entityData.partyRoleRef as Json | undefined;

    const sections: Record<string, SectionState> = {};
    for (const s of BI_SECTIONS) sections[s] = "Running";
    // The first step (company party) was just submitted to create the doc.
    sections.CompanyParty = "Completed";

    const doc: StoredDoc = {
      documentId,
      documentType: BI,
      etag: `"demo-etag-${documentId}-0"`,
      ts: Date.now(),
      customers: [],
      attachments: [],
      sections,
      submitActivityId: `act-submit-${documentId}`,
      submitActivityState: "Created",
      biEntities: partyRoleRef
        ? {
            partyRoleRef,
            partyRoleAdditionalInfo: buildAdditionalInfo(partyRoleRef),
          }
        : {},
      biProcessState: "Running",
      biMasterDocumentId: `demo-live-bi-${documentId}`,
    };
    store.set(documentId, doc);
    return serializeBi(doc);
  }

  if (documentType === FLOWPAY) {
    const entityData = (body.entityData ?? {}) as Json;
    const partyRoleRef = entityData.partyRoleRef as Json | undefined;
    const contactPersonRef = entityData.contactPersonRef as Json | undefined;

    const sections: Record<string, SectionState> = {};
    for (const s of FLOWPAY_SECTIONS) sections[s] = "Running";
    // CompanyParty was just submitted to create the doc.
    sections.CompanyParty = "Completed";

    const flowpayEntities: Record<string, Json> = {};
    if (partyRoleRef) {
      flowpayEntities.partyRoleRef = partyRoleRef;
      flowpayEntities.partyRoleAdditionalInfo =
        buildFlowpayAdditionalInfo(partyRoleRef);
    }
    if (contactPersonRef) flowpayEntities.contactPersonRef = contactPersonRef;

    const doc: StoredDoc = {
      documentId,
      documentType: FLOWPAY,
      etag: `"demo-etag-${documentId}-0"`,
      ts: Date.now(),
      customers: [],
      attachments: [],
      sections,
      submitActivityId: `act-submit-${documentId}`,
      submitActivityState: "Created",
      flowpayEntities,
      flowpayProcessState: "Running",
    };
    store.set(documentId, doc);
    return serializeFlowpay(doc);
  }

  // Company creation — onboarding step 1 (CompanyLookupPage submits a
  // ReqCreateMBMContract). Clear the demo onboarding flag so getUserContext
  // returns the full multi-company context again and the rest of the join flow
  // (connect bank / skip) lands back on the populated dashboard. Return the
  // partyRoleRef the create-company hook reads for its post-create navigation.
  if (documentType === "ReqCreateMBMContract") {
    finishDemoOnboarding();
    return {
      documentId,
      documentType,
      partyRoleRef: {
        entityData: {
          documentId: DEMO_COMPANY_HOLDINGS_AG,
          entityId: `${DEMO_COMPANY_HOLDINGS_AG}-entity`,
        },
      },
    };
  }

  if (documentType !== BIBBY) {
    const doc = createGenericDoc(documentId, documentType);
    store.set(documentId, doc);
    return serialize(doc);
  }

  const entityData = (body.entityData ?? {}) as Json;
  const partyRoleRef = entityData.partyRoleRef as Json | undefined;
  const contactPersonRef = entityData.contactPersonRef as Json | undefined;

  const sections: Record<string, SectionState> = {};
  for (const s of BIBBY_SECTIONS) sections[s] = "Running";
  // CompanyDetails was just submitted to create the doc.
  sections.CompanyDetails = "Completed";

  const doc: StoredDoc = {
    documentId,
    documentType: BIBBY,
    etag: `"demo-etag-${documentId}-0"`,
    ts: Date.now(),
    partyRoleRef,
    partyRoleAdditionalInfo: partyRoleRef
      ? buildAdditionalInfo(partyRoleRef)
      : undefined,
    contactPersonRef,
    customers: [],
    requestDetails: undefined,
    attachments: [],
    sections,
    submitActivityId: `act-submit-${documentId}`,
    submitActivityState: "Created",
  };
  store.set(documentId, doc);
  return serialize(doc);
}

export function contractsGetDocumentList(body: Json): Json {
  const readDocuments = (body.readDocuments ?? {}) as Json;
  const requestedTypes = Object.keys(readDocuments);

  // BusinessInsurance live contract — the payment page reads this by documentId
  // (resolved from the Req doc's masterDocumentReference). Serve a canned Live
  // doc carrying paymentInfo so the payment screen renders.
  if (requestedTypes.includes(BI_LIVE)) {
    // Top-level filtering for the getDocumentList of live contract.
    const filtering = (body.filtering ?? []) as Array<{
      filteringAttribute?: string;
      attributeValue?: unknown;
    }>;
    const wantedLiveId = filtering.find(
      (f) => f.filteringAttribute === "documentId",
    )?.attributeValue as string | undefined;
    if (!wantedLiveId) return emptyList();
    return {
      documentList: [serializeBiLive(wantedLiveId)],
      pagingResponse: {
        totalItemsCount: 1,
        hasNextPage: false,
        pageNumber: 1,
        pageSize: 20,
      },
    };
  }

  // FlowpayCredit — the wizard fetches "the first running contract" with no
  // filter and DataSubmission polls until flowpayCustomer is set. Resolve the
  // demo flowpayCustomer here so polling completes without the real backend.
  if (requestedTypes.includes(FLOWPAY)) {
    const flowpayDocs = [...store.values()].filter(
      (d) =>
        d.documentType === FLOWPAY && d.flowpayProcessState !== "Completed",
    );
    const documentList = flowpayDocs.map((d) => serializeFlowpay(d));
    return {
      documentList,
      pagingResponse: {
        totalItemsCount: documentList.length,
        hasNextPage: false,
        pageNumber: 1,
        pageSize: 20,
      },
    };
  }

  const managed = requestedTypes.find((t) => t === BIBBY || t === BI);

  // Not a managed Req*Contract type → behave like an empty list so other
  // callers of bw-contracts/getDocumentList aren't disrupted.
  if (!managed) return emptyList();

  // Filtering can live either under the per-docType config (Bibby) or at the
  // top level of the request (BusinessInsurance) — accept both.
  const docTypeConfig = (readDocuments[managed] ?? {}) as Json;
  const filtering = [
    ...((docTypeConfig.filtering ?? []) as Array<{
      filteringAttribute?: string;
      attributeValue?: unknown;
    }>),
    ...((body.filtering ?? []) as Array<{
      filteringAttribute?: string;
      attributeValue?: unknown;
    }>),
  ];
  const wantedDocId = filtering.find(
    (f) => f.filteringAttribute === "documentId",
  )?.attributeValue as string | undefined;
  const wantedProcessState = filtering.find(
    (f) => f.filteringAttribute === "processState",
  )?.attributeValue as string | undefined;

  let docs = [...store.values()].filter((d) => d.documentType === managed);
  if (wantedDocId) {
    docs = docs.filter((d) => d.documentId === wantedDocId);
  }

  if (managed === BI) {
    // Honour an explicit processState filter (e.g. the wizard's "Running" list);
    // without one (documentId lookups), return whatever matched.
    if (wantedProcessState) {
      docs = docs.filter(
        (d) => (d.biProcessState ?? "Running") === wantedProcessState,
      );
    }
    const documentList = docs.map((d) => serializeBi(d));
    return {
      documentList,
      pagingResponse: {
        totalItemsCount: documentList.length,
        hasNextPage: false,
        pageNumber: 1,
        pageSize: 20,
      },
    };
  }

  // The wizard fetches "the first running contract" — only surface docs that
  // are still in progress (lead not yet sent).
  docs = docs.filter((d) => d.submitActivityState !== "Completed");

  const documentList = docs.map((d) => serialize(d));
  return {
    documentList,
    pagingResponse: {
      totalItemsCount: documentList.length,
      hasNextPage: false,
      pageNumber: 1,
      pageSize: 20,
    },
  };
}

function emptyList(): Json {
  return {
    documentList: [],
    pagingResponse: {
      totalItemsCount: 0,
      hasNextPage: false,
      pageNumber: 1,
      pageSize: 20,
    },
  };
}

export function contractsCreateEntity(body: Json): Json {
  const doc = resolveDoc(body);
  if (doc.documentType === FLOWPAY) {
    applyFlowpayMutations(doc, (body.createEntity ?? {}) as Json);
    bumpEtag(doc);
    return serializeFlowpay(doc);
  }
  if (doc.documentType === BI) {
    applyBiMutations(doc, (body.createEntity ?? {}) as Json, { create: true });
    bumpEtag(doc);
    return serializeBi(doc);
  }
  if (doc.documentType !== BIBBY) return serialize(doc);

  const createEntity = (body.createEntity ?? {}) as Json;

  // Submit: processingInfo.createEntity.activities → ProductFormSubmit completed.
  if (createEntity.processingInfo) {
    doc.submitActivityState = "Completed";
    doc.leadId = doc.documentId;
    doc.sections.ReviewRequest = "Completed";
    bumpEtag(doc);
    return serialize(doc);
  }

  applyMutations(doc, createEntity, { create: true });
  bumpEtag(doc);
  return serialize(doc);
}

export function contractsUpdateEntity(body: Json): Json {
  const doc = resolveDoc(body);
  if (doc.documentType === FLOWPAY) {
    applyFlowpayMutations(doc, (body.updateEntity ?? {}) as Json);
    bumpEtag(doc);
    return serializeFlowpay(doc);
  }
  if (doc.documentType === BI) {
    applyBiMutations(doc, (body.updateEntity ?? {}) as Json, { create: false });
    bumpEtag(doc);
    return serializeBi(doc);
  }
  if (doc.documentType !== BIBBY) return serialize(doc);

  const updateEntity = (body.updateEntity ?? {}) as Json;
  applyMutations(doc, updateEntity, { create: false });
  bumpEtag(doc);
  return serialize(doc);
}

export function contractsDeleteEntity(body: Json): Json {
  const doc = resolveDoc(body);
  if (doc.documentType === FLOWPAY) {
    const deleteEntity = (body.deleteEntity ?? {}) as Json;
    for (const key of FLOWPAY_ENTITY_KEYS) {
      if (deleteEntity[key] && doc.flowpayEntities) {
        delete doc.flowpayEntities[key];
      }
    }
    bumpEtag(doc);
    return serializeFlowpay(doc);
  }
  if (doc.documentType === BI) {
    const deleteEntity = (body.deleteEntity ?? {}) as Json;
    // The only BI delete is additionalCoverage (when business interruption is
    // toggled off). Drop the stored entity so the contract reflects removal.
    for (const key of BI_ENTITY_KEYS) {
      if (deleteEntity[key] && doc.biEntities) delete doc.biEntities[key];
    }
    bumpEtag(doc);
    return serializeBi(doc);
  }
  if (doc.documentType !== BIBBY) return serialize(doc);

  const deleteEntity = (body.deleteEntity ?? {}) as Json;

  const custDel = deleteEntity.bibbyFactoringCustomerDetails as
    | { entityId?: string }
    | undefined;
  if (custDel?.entityId) {
    doc.customers = doc.customers.filter(
      (c) => c.entityId !== custDel.entityId,
    );
  }

  const attDel = deleteEntity.bibbyFactoringAttachment as
    | { entityId?: string }
    | undefined;
  if (attDel?.entityId) {
    doc.attachments = doc.attachments.filter(
      (a) => a.entityId !== attDel.entityId,
    );
  }

  bumpEtag(doc);
  return serialize(doc);
}

export function contractsGetSections(body: Json): Json {
  const requestedProducts = (body.requestedProducts ?? []) as string[];
  const sections: Json[] = [];

  for (const product of requestedProducts) {
    if (product === "BibbyFactoring") {
      for (const section of BIBBY_SECTIONS) {
        sections.push({
          contractSectionGroup: "BibbyFactoring",
          contractSection: section,
          isRequired: true,
          dataCollectionState: "Running",
        });
      }
    }
    if (product === "BusinessInsurance") {
      for (const section of BI_SECTIONS) {
        sections.push({
          contractSectionGroup: "BusinessInsurance",
          contractSection: section,
          isRequired: true,
          dataCollectionState: "Running",
        });
      }
    }
    if (product === "FlowpayCredit") {
      for (const section of FLOWPAY_SECTIONS) {
        sections.push({
          contractSectionGroup: "FlowpayCredit",
          contractSection: section,
          isRequired: true,
          dataCollectionState: "Running",
        });
      }
    }
  }

  // The bw-contracts adapter returns the response body directly, and the SDK
  // type for getContractSections is ContractSectionInfoGet[] (a bare array).
  // Returning an array (not { sections }) matches what the FE consumes.
  return sections as unknown as Json;
}

export function contractsUploadAttachment(body: Json): Json {
  const doc = resolveDoc(body);
  if (doc.documentType !== BIBBY) return serialize(doc);

  const createEntity = (body.createEntity ?? {}) as Json;
  const attachment = createEntity.bibbyFactoringAttachment as Json | undefined;
  const entityData = (attachment?.entityData ?? {}) as Json;
  const fileName =
    (entityData.fileName as string | undefined) ?? "document.pdf";

  attCounter += 1;
  const entityId = `att-${doc.documentId}-${attCounter}`;
  const blobStorageUrl = `demo-blob://${doc.documentId}/${entityId}/${encodeURIComponent(
    fileName,
  )}`;

  doc.attachments.push({
    entityId,
    entityData: {
      fileName,
      contentType: "application/pdf",
      blobStorageUrl,
    },
  });
  bumpEtag(doc);
  return serialize(doc);
}

export function contractsDownloadAttachment(_body: Json): Json {
  // The download SDK call returns void and the FE only opens a link; nothing
  // to do in demo mode.
  return { requestId: `demo-download-${Date.now()}` };
}

// ── Internals ───────────────────────────────────────────────────────────────

function resolveDoc(body: Json): StoredDoc {
  const documentId = getDocId(body);
  if (documentId && store.has(documentId)) {
    return store.get(documentId)!;
  }
  // Fall back to creating a doc so handlers never throw on an unknown id.
  const documentType = getDocType(body);
  const id = documentId ?? nextDocId();
  const doc =
    documentType === BIBBY
      ? bibbyShell(id)
      : documentType === BI
        ? biShell(id)
        : documentType === FLOWPAY
          ? flowpayShell(id)
          : createGenericDoc(id, documentType);
  store.set(id, doc);
  return doc;
}

function flowpayShell(documentId: string): StoredDoc {
  const sections: Record<string, SectionState> = {};
  for (const s of FLOWPAY_SECTIONS) sections[s] = "Running";
  return {
    documentId,
    documentType: FLOWPAY,
    etag: `"demo-etag-${documentId}-0"`,
    ts: Date.now(),
    customers: [],
    attachments: [],
    sections,
    submitActivityId: `act-submit-${documentId}`,
    submitActivityState: "Created",
    flowpayEntities: {},
    flowpayProcessState: "Running",
  };
}

function biShell(documentId: string): StoredDoc {
  const sections: Record<string, SectionState> = {};
  for (const s of BI_SECTIONS) sections[s] = "Running";
  return {
    documentId,
    documentType: BI,
    etag: `"demo-etag-${documentId}-0"`,
    ts: Date.now(),
    customers: [],
    attachments: [],
    sections,
    submitActivityId: `act-submit-${documentId}`,
    submitActivityState: "Created",
    biEntities: {},
    biProcessState: "Running",
    biMasterDocumentId: `demo-live-bi-${documentId}`,
  };
}

function bibbyShell(documentId: string): StoredDoc {
  const sections: Record<string, SectionState> = {};
  for (const s of BIBBY_SECTIONS) sections[s] = "Running";
  return {
    documentId,
    documentType: BIBBY,
    etag: `"demo-etag-${documentId}-0"`,
    ts: Date.now(),
    customers: [],
    attachments: [],
    sections,
    submitActivityId: `act-submit-${documentId}`,
    submitActivityState: "Created",
  };
}

function createGenericDoc(documentId: string, documentType: string): StoredDoc {
  return {
    documentId,
    documentType,
    etag: `"demo-etag-${documentId}-0"`,
    ts: Date.now(),
    customers: [],
    attachments: [],
    sections: {},
    submitActivityId: `act-submit-${documentId}`,
    submitActivityState: "Created",
  };
}

function buildAdditionalInfo(partyRoleRef: Json): Json {
  const documentId = partyRoleRef.documentId as string | undefined;
  const companyMap = DEMO_COMPANY_BY_ID as Record<
    string,
    { name?: string; jurisdiction?: string } | undefined
  >;
  const company = documentId ? companyMap[documentId] : undefined;
  return {
    partyType: "LegalEntity",
    roleType: "ClientCompany",
    roleName: company?.name ?? "Demo Company",
    roleCountry: company?.jurisdiction ?? "CZ",
  };
}

// FlowpayCredit echoes a 3-letter roleCountry ("CZE") because the FE's
// toFlowpayCountry maps the alpha-3 code to FlowPay's IsoCountryCode. roleName
// comes from the seeded company so the consent/embed reads a real name.
function buildFlowpayAdditionalInfo(partyRoleRef: Json): Json {
  const documentId = partyRoleRef.documentId as string | undefined;
  const companyMap = DEMO_COMPANY_BY_ID as Record<
    string,
    { name?: string } | undefined
  >;
  const company = documentId ? companyMap[documentId] : undefined;
  return {
    partyType: "LegalEntity",
    roleType: "ClientCompany",
    roleName: company?.name ?? "Demo Company",
    roleCountry: "CZE",
  };
}

type MutationOpts = { create: boolean };

function applyMutations(doc: StoredDoc, payload: Json, opts: MutationOpts) {
  // partyRoleRef
  if (payload.partyRoleRef) {
    const entityData = (payload.partyRoleRef as Json).entityData as
      | Json
      | undefined;
    if (entityData) {
      doc.partyRoleRef = entityData;
      doc.partyRoleAdditionalInfo = buildAdditionalInfo(entityData);
    }
  }

  // contactPersonRef
  if (payload.contactPersonRef) {
    const entityData = (payload.contactPersonRef as Json).entityData as
      | Json
      | undefined;
    if (entityData) doc.contactPersonRef = entityData;
  }

  // bibbyFactoringCustomerDetails (create appends, update replaces by entityId)
  if (payload.bibbyFactoringCustomerDetails) {
    const entity = payload.bibbyFactoringCustomerDetails as Json;
    const entityData = (entity.entityData ?? {}) as Json;
    const entityId = entity.entityId as string | undefined;
    if (opts.create || !entityId) {
      custCounter += 1;
      doc.customers.push({ entityId: `cust-${custCounter}`, entityData });
    } else {
      const existing = doc.customers.find((c) => c.entityId === entityId);
      if (existing) existing.entityData = entityData;
      else doc.customers.push({ entityId, entityData });
    }
    // Keep CustomerDetails "Running" — the user can add/edit more customers.
    doc.sections.CustomerDetails = "Running";
  }

  // bibbyFactoringRequestDetails (singleton)
  if (payload.bibbyFactoringRequestDetails) {
    const entity = payload.bibbyFactoringRequestDetails as Json;
    doc.requestDetails = (entity.entityData ?? {}) as Json;
    doc.sections.FactoringRequest = "Completed";
  }
}

// ── BusinessInsurance mutations + serialisation ───────────────────────────────

function applyBiMutations(doc: StoredDoc, payload: Json, opts: MutationOpts) {
  void opts; // create vs update is identical here — single-instance merge.
  if (!doc.biEntities) doc.biEntities = {};

  // The Confirmation submit marker: processingInfo.createEntity.activities → flip
  // process state to Completed and Confirmation section to Completed.
  if (payload.processingInfo) {
    doc.submitActivityState = "Completed";
    doc.biProcessState = "Completed";
    doc.sections.Confirmation = "Completed";
    return;
  }

  for (const key of BI_ENTITY_KEYS) {
    const entity = payload[key] as { entityData?: Json } | undefined;
    if (!entity) continue;
    const entityData = (entity.entityData ?? {}) as Json;

    // Merge into existing single-instance entity. addresses (array) is replaced
    // wholesale by the caller, so a shallow merge of entityData is correct.
    const existing = (doc.biEntities[key] ?? {}) as Json;
    doc.biEntities[key] = { ...existing, ...entityData };

    // partyRoleRef also drives the additional-info echo.
    if (key === "partyRoleRef") {
      doc.biEntities.partyRoleAdditionalInfo = buildAdditionalInfo(entityData);
    }

    // Section transitions.
    if (key === "partyRoleRef") doc.sections.CompanyParty = "Completed";
    if (key === "businessArea") doc.sections.BusinessArea = "Completed";
    // Coverage stays Running while the user edits coverages/addresses.
    if (
      key === "businessInsurancePolicyEssential" ||
      key === "businessInsurancePolicySmart" ||
      key === "businessInsurancePolicyPremium"
    ) {
      doc.sections.Quote = "Completed";
    }
  }
}

// Wrap a single-instance stored entity in the { entityData, entityHeader } shape
// the FE readEntities consumer expects.
function biEntityWrap(entityType: string, entityData: Json): Json {
  return {
    entityData,
    entityHeader: {
      entityType,
      definitionVersion: "1.0.0",
      entityId: `${entityType}-entity`,
      entityContext: BI,
    },
  };
}

const BI_ENTITY_TYPE_MAP: Record<string, string> = {
  partyRoleRef: "PartyRoleRef",
  partyRoleAdditionalInfo: "PartyRoleAdditionalInfo",
  businessArea: "BusinessArea",
  coverageToBeInsured: "CoverageToBeInsured",
  buildingInsuranceCoverage: "BuildingInsuranceCoverage",
  inventoryInsuranceCoverage: "InventoryInsuranceCoverage",
  machineryInsuranceCoverage: "MachineryInsuranceCoverage",
  businessLiabilityInsuranceCoverage: "BusinessLiabilityInsuranceCoverage",
  additionalCoverage: "AdditionalCoverage",
  individualOffer: "IndividualOffer",
  businessInsurancePolicyEssential: "BusinessInsurancePolicy",
  businessInsurancePolicySmart: "BusinessInsurancePolicy",
  businessInsurancePolicyPremium: "BusinessInsurancePolicy",
  coverageContractDetails: "CoverageContractDetails",
};

function serializeBi(doc: StoredDoc): Json {
  const header = {
    id: doc.documentId,
    documentType: BI,
    definitionVersion: "1.0.0",
    _etag: doc.etag,
    _ts: doc.ts,
  };

  const contractSections = BI_SECTIONS.map((section, idx) => ({
    entityData: {
      contractSectionGroup: "BusinessInsurance",
      contractSection: section,
      isRequired: true,
      dataCollectionState: doc.sections[section] ?? "Running",
    },
    entityHeader: {
      entityType: "ContractSection",
      definitionVersion: "1.0.0",
      entityId: `section-${idx}-${section}`,
      entityContext: "DataCollectionState",
    },
  }));

  const out: Json = {
    documentType: BI,
    documentHeader: header,
    dataCollectionState: {
      entityData: {
        contractSections: {
          pagingResponse: {
            totalItemsCount: contractSections.length,
            hasNextPage: false,
            pageNumber: 1,
            pageSize: 20,
          },
          returnedItems: contractSections,
        },
      },
      entityHeader: {
        entityType: "DataCollectionState",
        definitionVersion: "1.0.0",
        entityContext: BI,
      },
    },
    processingInfo: {
      entityData: {
        processingBasicInfo: {
          entityData: {
            processState: doc.biProcessState ?? "Running",
          },
          entityHeader: {
            entityType: "ProcessingBasicInfo",
            definitionVersion: "1.0.0",
            entityContext: "ProcessingInfo",
          },
        },
        activities: {
          pagingResponse: {
            totalItemsCount: 1,
            hasNextPage: false,
            pageNumber: 1,
            pageSize: 20,
          },
          returnedItems: [
            {
              entityData: {
                activityBasicInfo: {
                  entityData: {
                    activity: "ProductFormSubmit",
                    activityState: doc.submitActivityState,
                  },
                  entityHeader: {
                    entityType: "ActivityBasicInfo",
                    definitionVersion: "1.0.0",
                    entityContext: "Activity",
                  },
                },
              },
              entityHeader: {
                entityType: "Activity",
                definitionVersion: "1.0.0",
                entityId: doc.submitActivityId,
                entityContext: "ProcessingInfo",
              },
            },
          ],
        },
      },
      entityHeader: {
        entityType: "ProcessingInfo",
        definitionVersion: "1.0.0",
        entityContext: BI,
      },
    },
  };

  // After submit the payment page resolves the live contract via this ref.
  if (doc.biProcessState === "Completed" && doc.biMasterDocumentId) {
    out.masterDocumentReference = {
      entityData: {
        documentId: doc.biMasterDocumentId,
        documentType: BI_LIVE,
      },
      entityHeader: {
        entityType: "MasterDocumentReference",
        definitionVersion: "1.0.0",
        entityContext: BI,
      },
    };
  }

  // Echo every stored single-instance entity.
  for (const [key, entityData] of Object.entries(doc.biEntities ?? {})) {
    out[key] = biEntityWrap(BI_ENTITY_TYPE_MAP[key] ?? key, entityData as Json);
  }

  return out;
}

// Canned Live contract for the payment page. Carries paymentInfo (installment
// amount, account, symbols) + contractBasicInfo so mapLiveContractToPaymentData
// + the payment screen render.
function serializeBiLive(documentId: string): Json {
  const contractNumber = `7${String(Date.now()).slice(-9)}`;
  return {
    documentType: BI_LIVE,
    documentHeader: {
      id: documentId,
      documentType: BI_LIVE,
      definitionVersion: "1.0.0",
      _etag: `"demo-etag-${documentId}"`,
      _ts: Math.floor(Date.now() / 1000),
    },
    contractBasicInfo: {
      entityData: {
        contractNumber,
        contractType: "Marketplace",
        productType: "BusinessInsurance",
        contractStatus: "Active",
      },
      entityHeader: {
        entityType: "ContractBasicInfo",
        definitionVersion: "1.0.0",
        entityContext: BI_LIVE,
      },
    },
    paymentInfo: {
      entityData: {
        contractNumber,
        amount: "4980",
        installmentValue: "4980",
        currency: "CZK",
        // accountNumber is "<accountNo>/<bankCode>" — mapLiveContractToPaymentData
        // splits on "/" and parses via parseLocalCZ to derive the IBAN.
        accountNumber: "2702016516/2010",
        bankAccount: { iban: "CZ6520100000002702016516", bic: "FIOBCZPP" },
        accountName: "Direct pojišťovna, a.s.",
        variableSymbol: contractNumber,
      },
      entityHeader: {
        entityType: "PaymentInfo",
        definitionVersion: "1.0.0",
        entityContext: BI_LIVE,
      },
    },
  };
}

// ── FlowpayCredit mutations + serialisation ───────────────────────────────────

function applyFlowpayMutations(doc: StoredDoc, payload: Json) {
  if (!doc.flowpayEntities) doc.flowpayEntities = {};

  for (const key of FLOWPAY_ENTITY_KEYS) {
    const entity = payload[key] as { entityData?: Json } | undefined;
    if (!entity) continue;
    const entityData = (entity.entityData ?? {}) as Json;

    const existing = (doc.flowpayEntities[key] ?? {}) as Json;
    doc.flowpayEntities[key] = { ...existing, ...entityData };

    // partyRoleRef also drives the additional-info echo + section completion.
    if (key === "partyRoleRef") {
      doc.flowpayEntities.partyRoleAdditionalInfo =
        buildFlowpayAdditionalInfo(entityData);
      doc.sections.CompanyParty = "Completed";
    }

    // flowpayConsent (Consent step submit) → Consent done, and the proc layer
    // would normally call serviceActivation to set flowpayCustomer. In demo we
    // short-circuit that: set the customer id immediately so the DataSubmission
    // page's poll resolves without the real FlowPay backend.
    if (key === "flowpayConsent") {
      doc.sections.Consent = "Completed";
      doc.flowpayEntities.flowpayCustomer = {
        flowpayCustomerId: DEMO_FLOWPAY_CUSTOMER_ID,
        state: "ACTIVE",
      };
      doc.sections.DataSubmission = "Completed";
      doc.flowpayProcessState = "Completed";
    }
  }
}

function flowpayEntityWrap(entityType: string, entityData: Json): Json {
  return {
    entityData,
    entityHeader: {
      entityType,
      definitionVersion: "1.0.0",
      entityId: `${entityType}-entity`,
      entityContext: FLOWPAY,
    },
  };
}

function serializeFlowpay(doc: StoredDoc): Json {
  const header = {
    id: doc.documentId,
    documentType: FLOWPAY,
    definitionVersion: "1.0.0",
    _etag: doc.etag,
    _ts: doc.ts,
  };

  const contractSections = FLOWPAY_SECTIONS.map((section, idx) => ({
    entityData: {
      contractSectionGroup: "FlowpayCredit",
      contractSection: section,
      isRequired: true,
      dataCollectionState: doc.sections[section] ?? "Running",
    },
    entityHeader: {
      entityType: "ContractSection",
      definitionVersion: "1.0.0",
      entityId: `section-${idx}-${section}`,
      entityContext: "DataCollectionState",
    },
  }));

  const out: Json = {
    documentType: FLOWPAY,
    documentHeader: header,
    contractBasicInfo: {
      entityData: {
        contractType: "Marketplace",
        productType: "FlowpayCredit",
        contractStatus:
          doc.flowpayProcessState === "Completed" ? "Active" : "Draft",
      },
      entityHeader: {
        entityType: "ContractBasicInfo",
        definitionVersion: "1.0.0",
        entityContext: FLOWPAY,
      },
    },
    dataCollectionState: {
      entityData: {
        contractSections: {
          pagingResponse: {
            totalItemsCount: contractSections.length,
            hasNextPage: false,
            pageNumber: 1,
            pageSize: 20,
          },
          returnedItems: contractSections,
        },
      },
      entityHeader: {
        entityType: "DataCollectionState",
        definitionVersion: "1.0.0",
        entityContext: FLOWPAY,
      },
    },
    processingInfo: {
      entityData: {
        processingBasicInfo: {
          entityData: {
            processState: doc.flowpayProcessState ?? "Running",
          },
          entityHeader: {
            entityType: "ProcessingBasicInfo",
            definitionVersion: "1.0.0",
            entityContext: "ProcessingInfo",
          },
        },
        activities: {
          pagingResponse: {
            totalItemsCount: 1,
            hasNextPage: false,
            pageNumber: 1,
            pageSize: 20,
          },
          returnedItems: [
            {
              entityData: {
                activityBasicInfo: {
                  entityData: {
                    activity: "ProductFormSubmit",
                    activityState: doc.submitActivityState,
                  },
                  entityHeader: {
                    entityType: "ActivityBasicInfo",
                    definitionVersion: "1.0.0",
                    entityContext: "Activity",
                  },
                },
              },
              entityHeader: {
                entityType: "Activity",
                definitionVersion: "1.0.0",
                entityId: doc.submitActivityId,
                entityContext: "ProcessingInfo",
              },
            },
          ],
        },
      },
      entityHeader: {
        entityType: "ProcessingInfo",
        definitionVersion: "1.0.0",
        entityContext: FLOWPAY,
      },
    },
  };

  // Echo every stored single-instance entity (partyRoleRef,
  // partyRoleAdditionalInfo, contactPersonRef, flowpayConsent, flowpayCustomer).
  for (const [key, entityData] of Object.entries(doc.flowpayEntities ?? {})) {
    out[key] = flowpayEntityWrap(
      FLOWPAY_ENTITY_TYPE_MAP[key] ?? key,
      entityData as Json,
    );
  }

  return out;
}

// ── Serialisation (readEntities-shaped) ───────────────────────────────────────

function serialize(doc: StoredDoc): Json {
  if (doc.documentType !== BIBBY) {
    return {
      documentType: doc.documentType,
      documentHeader: {
        id: doc.documentId,
        documentType: doc.documentType,
        definitionVersion: "1.0.0",
        _etag: doc.etag,
        _ts: doc.ts,
      },
    };
  }

  const header = {
    id: doc.documentId,
    documentType: BIBBY,
    definitionVersion: "1.0.0",
    _etag: doc.etag,
    _ts: doc.ts,
  };

  const contractSections = BIBBY_SECTIONS.map((section, idx) => ({
    entityData: {
      contractSectionGroup: "BibbyFactoring",
      contractSection: section,
      isRequired: true,
      dataCollectionState: doc.sections[section] ?? "Running",
    },
    entityHeader: {
      entityType: "ContractSection",
      definitionVersion: "1.0.0",
      entityId: `section-${idx}-${section}`,
      entityContext: "DataCollectionState",
    },
  }));

  const out: Json = {
    documentType: BIBBY,
    documentHeader: header,
    contractBasicInfo: {
      entityData: {
        contractType: "Marketplace",
        productType: "BibbyFactoring",
        contractStatus:
          doc.submitActivityState === "Completed" ? "LeadSent" : "Draft",
      },
      entityHeader: {
        entityType: "ContractBasicInfo",
        definitionVersion: "1.0.0",
        entityContext: BIBBY,
      },
    },
    dataCollectionState: {
      entityData: {
        contractSections: {
          pagingResponse: {
            totalItemsCount: contractSections.length,
            hasNextPage: false,
            pageNumber: 1,
            pageSize: 20,
          },
          returnedItems: contractSections,
        },
      },
      entityHeader: {
        entityType: "DataCollectionState",
        definitionVersion: "1.0.0",
        entityContext: BIBBY,
      },
    },
    bibbyFactoringCustomerDetails: {
      pagingResponse: {
        totalItemsCount: doc.customers.length,
        hasNextPage: false,
        pageNumber: 1,
        pageSize: 20,
      },
      returnedItems: doc.customers.map((c) => ({
        entityData: c.entityData,
        entityHeader: {
          entityType: "BibbyFactoringCustomerDetail",
          definitionVersion: "1.0.0",
          entityId: c.entityId,
          entityContext: BIBBY,
        },
      })),
    },
    bibbyFactoringAttachment: {
      pagingResponse: {
        totalItemsCount: doc.attachments.length,
        hasNextPage: false,
        pageNumber: 1,
        pageSize: 20,
      },
      returnedItems: doc.attachments.map((a) => ({
        entityData: a.entityData,
        entityHeader: {
          entityType: "DocumentAttachment",
          definitionVersion: "1.0.0",
          entityId: a.entityId,
          entityContext: BIBBY,
        },
      })),
    },
    processingInfo: {
      entityData: {
        activities: {
          pagingResponse: {
            totalItemsCount: 1,
            hasNextPage: false,
            pageNumber: 1,
            pageSize: 20,
          },
          returnedItems: [
            {
              entityData: {
                activityBasicInfo: {
                  entityData: {
                    activity: "ProductFormSubmit",
                    activityState: doc.submitActivityState,
                  },
                  entityHeader: {
                    entityType: "ActivityBasicInfo",
                    definitionVersion: "1.0.0",
                    entityContext: "Activity",
                  },
                },
                result: {
                  entityData: {},
                  entityHeader: {
                    entityType: "ActivityResult",
                    definitionVersion: "1.0.0",
                    entityContext: "Activity",
                  },
                },
              },
              entityHeader: {
                entityType: "Activity",
                definitionVersion: "1.0.0",
                entityId: doc.submitActivityId,
                entityContext: "ProcessingInfo",
              },
            },
          ],
        },
      },
      entityHeader: {
        entityType: "ProcessingInfo",
        definitionVersion: "1.0.0",
        entityContext: BIBBY,
      },
    },
  };

  if (doc.partyRoleRef) {
    out.partyRoleRef = {
      entityData: doc.partyRoleRef,
      entityHeader: {
        entityType: "PartyRoleRef",
        definitionVersion: "1.0.0",
        entityContext: BIBBY,
      },
    };
  }
  if (doc.partyRoleAdditionalInfo) {
    out.partyRoleAdditionalInfo = {
      entityData: doc.partyRoleAdditionalInfo,
      entityHeader: {
        entityType: "PartyRoleAdditionalInfo",
        definitionVersion: "1.0.0",
        entityContext: BIBBY,
      },
    };
  }
  if (doc.contactPersonRef) {
    out.contactPersonRef = {
      entityData: doc.contactPersonRef,
      entityHeader: {
        entityType: "ContactPersonRef",
        definitionVersion: "1.0.0",
        entityContext: BIBBY,
      },
    };
  }
  if (doc.requestDetails) {
    out.bibbyFactoringRequestDetails = {
      entityData: doc.requestDetails,
      entityHeader: {
        entityType: "BibbyFactoringRequestDetails",
        definitionVersion: "1.0.0",
        entityContext: BIBBY,
      },
    };
  }
  if (doc.leadId) {
    out.bibbyFactoringLeadId = {
      entityData: { leadId: doc.leadId },
      entityHeader: {
        entityType: "BibbyFactoringLeadId",
        definitionVersion: "1.0.0",
        entityContext: BIBBY,
      },
    };
  }

  return out;
}
